(() => {
  const data = window.DORIMM_FITMENT_DATA;
  if (!data) return;
  const i18n = window.DORIMM_I18N || {};
  const message = (key, fallback) => i18n[key] || fallback;
  const formatMessage = (key, fallback, values = {}) => Object.entries(values).reduce(
    (text, [name, value]) => text.replaceAll(`{{ ${name} }}`, String(value)),
    message(key, fallback),
  );
  const localizedPath = (path) => {
    const root = String(i18n.root || "/").replace(/\/$/, "");
    return `${root}${path}` || path;
  };

  const STORAGE_KEY = "dorimm_selected_vehicle_v2";
  const vehicles = data.vehicles.map((vehicle) => ({
    ...vehicle,
    years: Array.from(
      { length: vehicle.yearTo - vehicle.yearFrom + 1 },
      (_, index) => vehicle.yearFrom + index,
    ),
  }));
  const skuData = data.skus;
  const unique = (items) => [...new Set(items)].sort();
  const escapeHtml = (value) =>
    String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  const vehicleLabel = (vehicle) => {
    const year = vehicle.selectedYear || `${vehicle.yearFrom}–${vehicle.yearTo}`;
    return `${year} ${vehicle.make} ${vehicle.model} — ${vehicle.trim}`;
  };

  const findVehicle = (id) => vehicles.find((vehicle) => vehicle.id === id);
  const vehicleFromProfile = (profile) => {
    if (!profile || typeof profile !== "object") return null;
    const snapshot = profile.vehicle && typeof profile.vehicle === "object" ? profile.vehicle : {};
    const selection = profile.selection || snapshot.request || {};
    const labels = profile.labels && typeof profile.labels === "object" ? profile.labels : {};
    const selectedYear = Number(selection.year || snapshot.request?.year || snapshot.year);
    const canonicalMake = String(selection.make || snapshot.request?.make || snapshot.make || "").trim();
    const canonicalModel = String(selection.model || snapshot.request?.model || snapshot.model || "").trim();
    const canonicalTrim = String(selection.modification || selection.trim || selection.generation || snapshot.request?.modification || snapshot.trim || snapshot.generation || "").trim();
    if (!selectedYear || !canonicalMake || !canonicalModel) return null;
    const readable = (value) => String(value || "")
      .trim()
      .replaceAll("-", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
    const make = readable(labels.make) || readable(canonicalMake);
    const model = readable(labels.model) || readable(canonicalModel);
    const trim = readable(labels.modification || labels.trim || labels.generation) || readable(canonicalTrim);
    const matches = Array.isArray(profile.matches) ? profile.matches : [];
    const boltCount = Number(snapshot.boltCount || snapshot.lugCount) || null;
    const pcdValue = snapshot.pcd ?? snapshot.boltCircle ?? "";
    const pcd = boltCount && pcdValue ? `${boltCount}×${pcdValue}` : String(pcdValue || "");
    const id = String(profile.vehicleId || profile.id || snapshot.id || `worker:${selectedYear}:${canonicalMake}:${canonicalModel}:${canonicalTrim}`)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    return {
      id,
      selectedYear,
      yearFrom: selectedYear,
      yearTo: selectedYear,
      make,
      model,
      trim: trim || message("selectedVehicleLabel", "Selected vehicle"),
      pcd,
      lugCount: boltCount,
      hubBore: Number(snapshot.centerBore || snapshot.hubBore) || "",
      chassis: String(selection.region || snapshot.chassis || ""),
      drive: String(snapshot.drive || ""),
      runtimeMatches: matches,
      runtimeMatchAuthoritative: Array.isArray(profile.matches),
    };
  };
  const saveVehicle = (vehicle, selectedYear = vehicle.selectedYear) => {
    try {
      // This helper is only used by the small built-in fallback catalogue. A
      // deliberate local selection must supersede any older Worker profile.
      localStorage.removeItem("dorimm_vehicle_profile_v1");
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ id: vehicle.id, year: Number(selectedYear) || null, savedAt: Date.now() }),
      );
    } catch (_error) {
      // Storage can be unavailable in strict privacy contexts.
    }
  };
  const clearVehicle = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem("dorimm_vehicle_profile_v1");
    } catch (_error) {
      // Storage can be unavailable in strict privacy contexts.
    }
    document.dispatchEvent(new CustomEvent("dorimm:vehicle-cleared"));
  };
  const storedVehicle = () => {
    try {
      // A full Worker profile carries the authoritative match list and must
      // win over the legacy local snapshot when both were saved together.
      const profileRaw = localStorage.getItem("dorimm_vehicle_profile_v1");
      if (profileRaw !== null) {
        const profile = JSON.parse(profileRaw || "null");
        const profileVehicle = vehicleFromProfile(profile);
        if (profileVehicle) return profileVehicle;
        // Older builds could leave an invalid Worker profile alongside an
        // incorrect local fallback. Never revive that stale vehicle.
        localStorage.removeItem("dorimm_vehicle_profile_v1");
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      const vehicle = findVehicle(stored?.id);
      if (vehicle) return { ...vehicle, selectedYear: Number(stored?.year) || vehicle.yearFrom };
      return null;
    } catch (_error) {
      return null;
    }
  };
  const selectedVehicle = () => {
    const params = new URLSearchParams(window.location.search);
    const urlVehicle = findVehicle(params.get("vehicle_id"));
    if (urlVehicle) {
      const selectedYear = Number(params.get("vehicle_year")) || urlVehicle.yearFrom;
      saveVehicle(urlVehicle, selectedYear);
      return { ...urlVehicle, selectedYear };
    }
    return storedVehicle();
  };
  const fitmentForSku = (sku, vehicleId) =>
    skuData[sku]?.fitments.find((fitment) => fitment.vehicleId === vehicleId);
  const matchingSkus = (skus, vehicleId) =>
    skus.filter((sku) => Boolean(fitmentForSku(sku, vehicleId)));

  const closeMenus = (except = null) => {
    document.querySelectorAll("[data-custom-select].is-open").forEach((control) => {
      if (control === except) return;
      control.classList.remove("is-open");
      control
        .querySelector("[data-select-trigger]")
        ?.setAttribute("aria-expanded", "false");
      const menu = control.querySelector("[data-select-menu]");
      if (menu) menu.hidden = true;
    });
  };

  const syncCustomSelect = (select) => {
    const control = select.closest("[data-custom-select]");
    if (!control) return;
    const trigger = control.querySelector("[data-select-trigger]");
    const value = control.querySelector("[data-select-value]");
    const menu = control.querySelector("[data-select-menu]");
    const selected = select.options[select.selectedIndex];

    trigger.disabled = select.disabled;
    value.textContent =
      selected?.textContent || select.options[0]?.textContent || message("selectLabel", "Select");
    menu.innerHTML = "";

    [...select.options]
      .filter((option) => option.value)
      .forEach((option) => {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "finder-option";
        item.setAttribute("role", "option");
        item.setAttribute("aria-selected", String(option.value === select.value));
        item.dataset.value = option.value;
        item.textContent = option.textContent;
        item.addEventListener("click", () => {
          select.value = option.value;
          syncCustomSelect(select);
          closeMenus();
          select.dispatchEvent(new Event("change", { bubbles: true }));
        });
        menu.appendChild(item);
      });

    if (select.disabled || !menu.children.length) {
      control.classList.remove("is-open");
      trigger.setAttribute("aria-expanded", "false");
      menu.hidden = true;
    }
  };

  const enhanceCustomSelect = (select) => {
    const control = select.closest("[data-custom-select]");
    if (!control || control.dataset.ready === "true") return;
    control.dataset.ready = "true";
    const trigger = control.querySelector("[data-select-trigger]");
    const menu = control.querySelector("[data-select-menu]");

    trigger.addEventListener("click", () => {
      if (trigger.disabled) return;
      const willOpen = !control.classList.contains("is-open");
      closeMenus(control);
      control.classList.toggle("is-open", willOpen);
      trigger.setAttribute("aria-expanded", String(willOpen));
      menu.hidden = !willOpen;
      if (willOpen) menu.querySelector('[aria-selected="true"]')?.focus();
    });
    menu.addEventListener("keydown", (event) => {
      const options = [...menu.querySelectorAll('[role="option"]')];
      if (!options.length) return;
      const index = Math.max(0, options.indexOf(document.activeElement));
      let target = null;
      if (event.key === "ArrowDown") target = options[Math.min(options.length - 1, index + 1)];
      if (event.key === "ArrowUp") target = options[Math.max(0, index - 1)];
      if (event.key === "Home") target = options[0];
      if (event.key === "End") target = options.at(-1);
      if (event.key === "Escape") {
        closeMenus();
        trigger.focus();
        return;
      }
      if (target) {
        event.preventDefault();
        target.focus();
      }
    });
    syncCustomSelect(select);
  };

  const setOptions = (select, placeholder, items) => {
    select.innerHTML = "";
    const prompt = document.createElement("option");
    prompt.value = "";
    prompt.textContent = placeholder;
    select.appendChild(prompt);
    items.forEach((item) => {
      const option = document.createElement("option");
      option.value = String(item);
      option.textContent = String(item);
      select.appendChild(option);
    });
    select.disabled = !items.length;
    syncCustomSelect(select);
  };

  const initFinder = (finder) => {
    if (finder.dataset.fitmentReady === "true") return;
    finder.dataset.fitmentReady = "true";

    const year = finder.querySelector("[data-year]");
    const make = finder.querySelector("[data-make]");
    const model = finder.querySelector("[data-model]");
    const trim = finder.querySelector("[data-trim]");
    const submit = finder.querySelector("[data-find]");
    const note = finder.querySelector("[data-fitment-note]");
    const result = finder.querySelector("[data-fitment-result]");

    [year, make, model, trim].forEach(enhanceCustomSelect);

    const matchingRows = () =>
      vehicles.filter(
        (vehicle) =>
          vehicle.years.includes(Number(year.value)) &&
          (!make.value || vehicle.make === make.value) &&
          (!model.value || vehicle.model === model.value) &&
          (!trim.value || vehicle.trim === trim.value),
      );

    const resetResult = () => {
      result.hidden = true;
      result.innerHTML = "";
    };

    const renderResult = (vehicle) => {
      const hasCandidates = vehicle.candidateSkus > 0;
      result.innerHTML = `
        <div class="fitment-result-head">
          <div>
            <small>${escapeHtml(vehicle.chassis)} · ${escapeHtml(vehicle.drive)}</small>
            <strong>${vehicle.candidateSkus} ${escapeHtml(message("matchedConfigurations", "matched configurations"))} · ${vehicle.candidateProducts} ${escapeHtml(message("wheelStyles", "wheel styles"))}</strong>
          </div>
          <span>${escapeHtml(hasCandidates ? message("engineeringMatch", "ENGINEERING MATCH") : message("noCurrentMatch", "NO CURRENT MATCH"))}</span>
        </div>
        <div class="fitment-result-specs">
          <span><small>PCD</small><b>${escapeHtml(vehicle.pcd)}</b></span>
          <span><small>HUB</small><b>${vehicle.hubBore} mm</b></span>
          <span><small>MIN.</small><b>${vehicle.minDiameter}&quot;</b></span>
          <span><small>OE FRONT</small><b>${escapeHtml(vehicle.oeFront)}</b></span>
          <span><small>OE REAR</small><b>${escapeHtml(vehicle.oeRear)}</b></span>
          <span><small>HARDWARE</small><b>${escapeHtml(vehicle.hardware)} · ${escapeHtml(vehicle.seat)}</b></span>
        </div>
        <p><b>${escapeHtml(hasCandidates ? message("reviewRequiredSentence", "Dorimm review required.") : message("noLaunchRule", "No current launch configuration passes the rule set."))}</b>
        ${escapeHtml(hasCandidates ? message("resultsRequireReview", data.policy.notice) : message("manualSourcingReview", "Contact Dorimm for a manual sourcing review."))}</p>
      `;
      result.hidden = false;
      submit.disabled = false;
      submit.querySelector("span").textContent = hasCandidates
        ? formatMessage("viewMatches", "VIEW {{ count }} MATCHES", { count: vehicle.candidateSkus })
        : message("viewFitmentStatus", "VIEW FITMENT STATUS");
    };

    setOptions(
      year,
      message("selectYear", "Select year"),
      unique(vehicles.flatMap((vehicle) => vehicle.years)).sort((a, b) => b - a),
    );
    setOptions(make, message("selectMake", "Select make"), []);
    setOptions(model, message("selectModel", "Select model"), []);
    setOptions(trim, message("selectTrim", "Select trim"), []);

    year.addEventListener("change", () => {
      if (year.value) document.dispatchEvent(new CustomEvent("dorimm:analytics", { detail: { event: "fitment_started", source: "vehicle_finder" } }));
      const rows = matchingRows();
      setOptions(make, message("selectMake", "Select make"), unique(rows.map((vehicle) => vehicle.make)));
      setOptions(model, message("selectModel", "Select model"), []);
      setOptions(trim, message("selectTrim", "Select trim"), []);
      submit.disabled = true;
      resetResult();
      note.textContent = year.value
        ? message("chooseMakeNext", "Now choose your vehicle make.")
        : message("startSelectYear", "Start by selecting your vehicle year.");
    });

    make.addEventListener("change", () => {
      const rows = matchingRows();
      setOptions(model, message("selectModel", "Select model"), unique(rows.map((vehicle) => vehicle.model)));
      setOptions(trim, message("selectTrim", "Select trim"), []);
      submit.disabled = true;
      resetResult();
      note.textContent = message("selectModelTrim", "Select your model and trim.");
    });

    model.addEventListener("change", () => {
      const rows = matchingRows();
      setOptions(trim, message("selectTrim", "Select trim"), unique(rows.map((vehicle) => vehicle.trim)));
      submit.disabled = true;
      resetResult();
    });

    trim.addEventListener("change", () => {
      const vehicle = matchingRows()[0];
      if (vehicle) {
        renderResult(vehicle);
        document.dispatchEvent(new CustomEvent("dorimm:analytics", { detail: { event: "fitment_completed", vehicle_id: vehicle.id, candidate_skus: vehicle.candidateSkus, candidate_products: vehicle.candidateProducts } }));
        note.textContent = message("resultsRequireReview", "Results are SKU-level engineering matches and require Dorimm review.");
      } else {
        submit.disabled = true;
        resetResult();
        note.textContent = message("selectTrim", "Select trim");
      }
    });

    submit.addEventListener("click", () => {
      const vehicle = matchingRows()[0];
      if (!vehicle) return;
      saveVehicle(vehicle, year.value);
      document.dispatchEvent(new CustomEvent("dorimm:analytics", { detail: { event: "fitment_results_viewed", vehicle_id: vehicle.id, candidate_skus: vehicle.candidateSkus } }));
      window.location.href = `${localizedPath("/collections/all")}?vehicle_id=${encodeURIComponent(vehicle.id)}&vehicle_year=${encodeURIComponent(year.value)}`;
    });
  };

  const initCollectionFitment = (context) => {
    if (context.dataset.fitmentReady === "true") return;
    context.dataset.fitmentReady = "true";
    const vehicle = selectedVehicle();
    if (!vehicle) return;

    const collection = context.closest("[data-collection-experience]");
    const cards = [...(collection || document).querySelectorAll("[data-product-card]")];
    const runtimeSkuSet = new Set(
      vehicle.runtimeMatchAuthoritative
        ? (vehicle.runtimeMatches || []).map((match) => String(match?.sku || "")).filter(Boolean)
        : [],
    );
    let matchedProducts = 0;
    let matchedConfigurations = 0;
    cards.forEach((card) => {
      const skus = (card.dataset.productSkus || "").split(",").filter(Boolean);
      const matches = vehicle.runtimeMatchAuthoritative
        ? skus.filter((sku) => runtimeSkuSet.has(String(sku)))
        : matchingSkus(skus, vehicle.id);
      const matched = matches.length > 0;
      card.dataset.fitmentMatch = String(matched);
      card.classList.toggle("is-garage-match", matched);
      if (matched) matchedProducts += 1;
      matchedConfigurations += matches.length;
      const badge = card.querySelector("[data-card-fitment]");
      if (badge) {
        badge.hidden = !matched;
        if (matched) badge.textContent = `${matches.length} ${message(matches.length === 1 ? "dimensionalCandidateLabel" : "dimensionalCandidates", matches.length === 1 ? "dimensional candidate" : "DIMENSIONAL CANDIDATES")}`;
      }
      if (!matched) return;
      card.querySelectorAll("a[href]").forEach((cardLink) => {
        if (!cardLink.dataset.fitmentBaseHref) {
          cardLink.dataset.fitmentBaseHref = cardLink.getAttribute("href") || cardLink.href;
        }
        const url = new URL(cardLink.href, window.location.origin);
        url.searchParams.set("vehicle_id", vehicle.id);
        url.searchParams.set("vehicle_year", vehicle.selectedYear || vehicle.yearFrom);
        url.searchParams.set("sku", matches[0]);
        cardLink.href = `${url.pathname}${url.search}`;
      });
    });

    context.innerHTML = `
      <div>
        <small>${escapeHtml(message("myGarageActive", "MY GARAGE / ACTIVE VEHICLE"))}</small>
        <strong>${escapeHtml(vehicleLabel(vehicle))}</strong>
        <span>${escapeHtml(vehicle.pcd)} · CB ${vehicle.hubBore} mm · ${escapeHtml(vehicle.chassis)}</span>
      </div>
      <div class="collection-fitment-specs">
        <span><small>${escapeHtml(message("wheelStyles", "WHEEL STYLES"))}</small><b>${matchedProducts}</b></span>
        <span><small>${escapeHtml(message("configurations", "CONFIGURATIONS"))}</small><b>${matchedConfigurations}</b></span>
      </div>
      <p><b>${escapeHtml(message("dimensionalCandidates", "DIMENSIONAL CANDIDATES"))}</b>${matchedConfigurations ? escapeHtml(message("resultsRequireReview", data.policy.notice)) : escapeHtml(message("noLaunchForVehicle", "No current launch configuration passes the dimensional rule set for this vehicle."))}</p>
      <div class="collection-fitment-actions">
        <button class="is-active" type="button" data-fitment-mode="matches">${escapeHtml(message("showMatches", "SHOW MATCHES"))}</button>
        <button type="button" data-fitment-mode="all">${escapeHtml(message("showAll", "SHOW ALL"))}</button>
        <a href="${escapeHtml(localizedPath("/pages/fitment"))}" data-clear-vehicle>${escapeHtml(message("changeVehicle", "CHANGE VEHICLE"))}</a>
      </div>
    `;
    context.hidden = false;
    if (collection) collection.dataset.collectionMatchMode = "matches";
    context.querySelector("[data-clear-vehicle]").addEventListener("click", clearVehicle);
    context.querySelectorAll("button[data-fitment-mode]").forEach((button) => {
      button.addEventListener("click", () => {
        const mode = button.dataset.fitmentMode;
        context.querySelectorAll("button[data-fitment-mode]").forEach((item) => item.classList.toggle("is-active", item === button));
        if (collection) {
          collection.dataset.collectionMatchMode = mode;
          collection.dispatchEvent(new CustomEvent("dorimm:collection-context", { detail: { mode, matchedProducts } }));
        }
      });
    });
  };

  const parseVariants = (root) => {
    try {
      return JSON.parse(
        root.querySelector("[data-product-variants]")?.textContent || "[]",
      );
    } catch (_error) {
      return [];
    }
  };

  const localizedFinish = (value) => {
    const labels = {
      "Gloss Black": message("glossBlack", "Gloss Black"),
      "Matte Black": message("matteBlack", "Matte Black"),
      Bronze: message("bronze", "Bronze"),
      "Black Machined": message("blackMachined", "Black Machined"),
      Gunmetal: message("gunmetal", "Gunmetal"),
      "Hyper Silver": message("hyperSilver", "Hyper Silver"),
      White: message("white", "White"),
      "Chrome / Polished": message("chromePolished", "Chrome polished"),
      "Chrome Polished": message("chromePolished", "Chrome polished"),
      "Satin Bronze": message("satinBronze", "Satin Bronze"),
      "Custom Finish": message("customFinish", "Custom finish"),
    };
    return labels[String(value || "")] || String(value || "");
  };

  const localizedVariantSummary = (variant) => {
    const options = Array.isArray(variant?.options) ? variant.options : [];
    const size = options.find((value) => /^\s*\d+(?:\.\d+)?\s*[x×]\s*\d+/i.test(value)) || "";
    const fitment = options.find((value) => /(?:ET|CB|\d+\s*[x×]\s*\d{2,3})/i.test(value) && value !== size) || "";
    const finish = options.find((value) => value !== size && value !== fitment) || variant?.specs?.finish || "";
    return [size, fitment, localizedFinish(finish)].filter(Boolean).join(" / ");
  };

  const setProperty = (form, name, value) => {
    const input = form.querySelector(`[data-fitment-property="${name}"]`);
    if (input) input.value = value ?? "";
  };

  const firstDefined = (...values) => values.find(
    (value) => value !== null && value !== undefined && String(value).trim() !== "",
  );

  const formatSpecNumber = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? String(numeric) : String(value ?? "").trim();
  };

  const exactSpecsForVariant = (variant) => {
    if (!variant) return { complete: false, summary: "" };
    const options = Array.isArray(variant.options) ? variant.options : [];
    const sizeOption = options.find((value) => /^\s*\d+(?:\.\d+)?\s*[x×]\s*\d+/i.test(value)) || "";
    const fitmentOption = options.find(
      (value) => /(?:ET|CB|\d+\s*[x×]\s*\d{2,3})/i.test(value) && value !== sizeOption,
    ) || "";
    const sizeMatch = sizeOption.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    const pcdMatch = fitmentOption.match(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    const offsetMatch = fitmentOption.match(/ET\s*(-?\d+(?:\.\d+)?)/i);
    const centerBoreMatch = fitmentOption.match(/CB\s*(\d+(?:\.\d+)?)/i);
    const variantSpecs = Object.fromEntries(
      Object.entries(variant.specs || {}).filter(
        ([, value]) => value !== null && value !== undefined && String(value).trim() !== "",
      ),
    );
    const source = { ...(skuData[variant.sku] || {}), ...variantSpecs };
    const sourcePcdMatch = String(source.pcd || "").match(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    const diameter = firstDefined(source.diameter, source.diameterIn, sizeMatch?.[1]);
    const width = firstDefined(source.width, source.widthIn, sizeMatch?.[2]);
    const boltCount = firstDefined(source.boltCount, source.lugCount, sourcePcdMatch?.[1], pcdMatch?.[1]);
    const pcdMm = firstDefined(source.pcdMm, source.boltCircle, sourcePcdMatch?.[2], pcdMatch?.[2]);
    const offsetMm = firstDefined(source.offsetMm, source.et, offsetMatch?.[1]);
    const centerBoreMm = firstDefined(source.centerBoreMm, source.cb, centerBoreMatch?.[1]);
    const finish = firstDefined(
      source.finish,
      options.find((value) => value !== sizeOption && value !== fitmentOption),
    );
    const numericSpecs = [diameter, width, boltCount, pcdMm, offsetMm, centerBoreMm];
    const complete = Boolean(
      String(variant.sku || "").trim()
      && String(finish || "").trim()
      && numericSpecs.every((value) => Number.isFinite(Number(value)))
      && Number(diameter) > 0
      && Number(width) > 0
      && Number(boltCount) > 0
      && Number(pcdMm) > 0
      && Number(centerBoreMm) > 0,
    );
    const summary = complete
      ? [
          `${formatSpecNumber(diameter)}×${formatSpecNumber(width)}`,
          `${formatSpecNumber(boltCount)}×${formatSpecNumber(pcdMm)}`,
          `ET${formatSpecNumber(offsetMm)}`,
          `CB${formatSpecNumber(centerBoreMm)}`,
          localizedFinish(finish),
          variant.sku,
        ].join(" · ")
      : localizedVariantSummary(variant);
    return {
      diameter,
      width,
      boltCount,
      pcdMm,
      offsetMm,
      centerBoreMm,
      finish,
      complete,
      summary,
    };
  };

  const updateProductSpecs = (root, variant) => {
    if (!variant) return;
    const variantSpecs = Object.fromEntries(
      Object.entries(variant.specs || {}).filter(([, value]) => value !== null && value !== undefined && value !== ""),
    );
    const source = { ...(skuData[variant.sku] || {}), ...variantSpecs };
    const options = Array.isArray(variant.options) ? variant.options : [];
    const sizeOption = options.find((value) => /^\s*\d+(?:\.\d+)?\s*[x×]\s*\d+/i.test(value)) || "";
    const fitmentOption = options.find((value) => /(?:ET|CB|\d+\s*[x×]\s*\d{2,3})/i.test(value) && value !== sizeOption) || "";
    const size = sizeOption.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    const pcd = fitmentOption.match(/(\d+)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
    const offset = fitmentOption.match(/ET\s*(-?\d+(?:\.\d+)?)/i);
    const centerBore = fitmentOption.match(/CB\s*(\d+(?:\.\d+)?)/i);
    const finish = options.find((value) => value !== sizeOption && value !== fitmentOption) || "";
    const diameterValue = Number(source?.diameter ?? size?.[1]);
    const widthValue = Number(source?.width ?? size?.[2]);
    const offsetValue = Number(source?.offsetMm ?? source?.et ?? offset?.[1]);
    const estimatedWeight = root.dataset.weightVaries || message("variesBySize", "Varies by selected size");
    const backspacing = (() => {
      if (!Number.isFinite(widthValue) || !Number.isFinite(offsetValue)) return "";
      const inches = ((widthValue + 1) / 2) + (offsetValue / 25.4);
      return `${inches.toFixed(2)} in / ${Math.round(inches * 25.4)} mm`;
    })();
    const hasSource = Object.keys(source).length > 0;
    const weightStatus = String(source.weightStatus || "");
    const referenceWeight = source.referenceWeightKg ?? source.weightKg;
    const verifiedWeight = /^(VERIFIED|REPORT_BACKED|REPORT_VERIFIED|MEASURED)$/i.test(weightStatus)
      ? referenceWeight
      : null;
    const approximateWeight = /^REFERENCE_PROVISIONAL_NOT_STOREFRONT$/i.test(weightStatus)
      ? referenceWeight
      : null;
    const displayWeight = verifiedWeight != null
      ? `${verifiedWeight} kg`
      : approximateWeight != null
        ? `${root.dataset.weightApprox || message("approximate", "Approx.")} ${approximateWeight} kg`
        : estimatedWeight;
    const specs = hasSource ? {
      diameter: source.diameter ? `${source.diameter}"` : "",
      width: source.width ? `${source.width}"` : "",
      pcd: source.pcd || (source.boltCount && source.pcdMm ? `${source.boltCount}×${source.pcdMm}` : ""),
      offset: (source.offsetMm ?? source.et) != null ? `ET${source.offsetMm ?? source.et}` : "",
      center_bore: (source.centerBoreMm ?? source.cb) ? `${source.centerBoreMm ?? source.cb} mm` : "",
      weight: displayWeight,
      material: root.dataset.material || "",
      backspacing: source.backspacingIn ? `${source.backspacingIn} in` : backspacing,
      construction: source.process || root.dataset.construction || "",
      finish: source.finish || finish,
      stock: message("madeToOrder", "Made to order · 7–15 day production"),
    } : {
      diameter: size?.[1] ? `${size[1]}"` : "",
      width: size?.[2] ? `${size[2]}"` : "",
      pcd: pcd ? `${pcd[1]}×${pcd[2]}` : "",
      offset: offset ? `ET${offset[1]}` : "",
      center_bore: centerBore ? `${centerBore[1]} mm` : "",
      weight: estimatedWeight,
      material: root.dataset.material || "",
      backspacing,
      construction: root.dataset.construction || "",
      finish,
      stock: message("madeToOrder", "Made to order · 7–15 day production"),
    };
    const constructionValue = String(specs.construction || "").trim();
    const manufacturingConflict = /^(cast|flow[ -]?formed)$/i.test(constructionValue);
    root.dataset.manufacturingConflict = String(manufacturingConflict);
    const valueLabels = {
      "Forged": message("forged", "Forged monoblock"),
      "Forged monoblock": message("forged", "Forged monoblock"),
    };
    specs.construction = valueLabels[specs.construction] || specs.construction;
    specs.finish = localizedFinish(specs.finish);
    const configSummary = root.querySelector("[data-product-config-summary]");
    if (configSummary) {
      configSummary.textContent = [sizeOption, fitmentOption, specs.finish].filter(Boolean).join(" / ");
    }
    Object.entries(specs).forEach(([key, value]) => {
      const target = root.querySelector(`[data-product-spec="${key}"]`);
      if (!target) return;
      target.textContent = value;
      const specRow = target.closest("[data-spec-row]");
      if (!specRow) return;
      specRow.hidden = !value;
      specRow.classList.toggle("is-long-value", String(value).length > 14);
      specRow.classList.toggle("is-very-long-value", String(value).length > 25);
      root.querySelectorAll(`[data-product-spec-mirror="${key}"]`).forEach((mirror) => {
        mirror.textContent = value || "—";
      });
    });
  };

  const initProductFitment = (root) => {
    if (root.dataset.fitmentReady === "true") return;
    root.dataset.fitmentReady = "true";
    root.dataset.pdpFitmentOwner = "dorimm-fitment";
    const form = root.querySelector(".dorimm-product-form");
    if (!form) return;

    const variantSelect = form.querySelector("[name='id']");
    const optionSelectors = [...form.querySelectorAll("[data-product-option]")];
    const variants = parseVariants(root);
    const panel = root.querySelector("[data-product-fitment]");
    const atc = form.querySelector("[data-product-atc]");
    const buyNow = form.querySelector("[data-product-buy-now]");
    const purchaseAction = form.querySelector("[data-purchase-action]");
    const purchasePrompt = form.querySelector("[data-product-atc-note]");
    const vehicleCta = form.querySelector("[data-product-vehicle-cta]");
    const vehicleConsent = form.querySelector("[data-vehicle-consent], [data-fitment-consent]");
    const expertConsent = form.querySelector("[data-expert-spec-consent], [data-expert-consent]");
    const flowBody = form.querySelector("[data-product-flow-body]");
    const expertReview = form.querySelector("[data-expert-review]");
    const expertSummary = form.querySelector("[data-expert-spec-summary], [data-expert-summary]");
    const expertConfirm = form.querySelector("[data-expert-spec-confirm]");
    const price = root.querySelector("[data-product-price]");
    const routeButtons = [...root.querySelectorAll("[data-product-route]")];
    const quantityInput = form.querySelector("[data-quantity-value]");
    const quantityPreset = form.querySelector("[data-quantity-preset]");
    const customQuantity = form.querySelector("[data-custom-quantity]");
    const totalPrice = root.querySelector("[data-product-total-price]");
    const purchaseTotal = form.querySelector("[data-product-atc-total]");

    if (!variantSelect || !panel || !atc) {
      const initialVariant = variants.find((variant) => String(variant.id) === String(variantSelect?.value)) || variants[0];
      updateProductSpecs(root, initialVariant);
      return;
    }

    const normalizeRoute = (route) => {
      if (route === "vehicle") return "vehicle";
      if (route === "expert" || route === "spec") return "expert";
      return "";
    };
    const confirmationContainer = (control, fallbackSelector) => {
      if (!control) return null;
      if (control.matches(fallbackSelector)) return control;
      return control.closest(fallbackSelector) || control.closest("label") || control.parentElement;
    };
    const vehicleConfirm = confirmationContainer(vehicleConsent, "[data-vehicle-confirm], .fitment-confirm");
    const expertConfirmContainer = confirmationContainer(expertConfirm || expertConsent, "[data-expert-spec-confirm], [data-expert-confirm]");
    let vehicle = selectedVehicle();
    let purchaseRoute = vehicle ? "vehicle" : "";
    let confirmedExpertVariantId = "";
    const currency = root.dataset.currency || "USD";
    const money = new Intl.NumberFormat(document.documentElement.lang || "en-US", {
      style: "currency",
      currency,
    });
    const centerCapReady = () => form.dataset.centerCapComplete === "true";
    const centerCapPrompt = root.dataset.centerCapRequired || message("centerCapContinue", "SELECT CENTER CAP TO CONTINUE");
    const orderable = () => root.dataset.orderable === "true" && root.dataset.manufacturingConflict !== "true";
    const variantById = (id) => variants.find((variant) => String(variant.id) === String(id));
    const purchaseSteps = [...root.querySelectorAll(".product-purchase-path span")];
    const purchasePropertyKeys = [
      "vehicle_id",
      "vehicle",
      "status",
      "wheel_sku",
      "vehicle_pcd",
      "recommended_tire",
      "hub_ring",
      "hardware",
      "rule",
      "purchase_method",
      "expert_specification",
      "specification_confirmation",
      "purchase_route",
    ];

    let compatibleVariants = [];
    let compatibleIds = new Set();
    let runtimeMatchesBySku = new Map();
    let runtimeMatchesById = new Map();
    const normalizedVariantId = (value) => String(value || "").split("/").pop();
    const fitmentForVariant = (variant) => {
      if (!variant || !vehicle) return null;
      if (vehicle.runtimeMatchAuthoritative) {
        return runtimeMatchesBySku.get(String(variant.sku || ""))
          || runtimeMatchesById.get(normalizedVariantId(variant.id))
          || null;
      }
      return fitmentForSku(variant.sku, vehicle.id) || null;
    };
    const refreshVehicleContext = (nextVehicle) => {
      vehicle = nextVehicle === undefined ? selectedVehicle() : nextVehicle;
      const runtimeMatches = vehicle?.runtimeMatchAuthoritative && Array.isArray(vehicle.runtimeMatches)
        ? vehicle.runtimeMatches
        : [];
      runtimeMatchesBySku = new Map(
        runtimeMatches.filter((match) => match?.sku).map((match) => [String(match.sku), match]),
      );
      runtimeMatchesById = new Map(
        runtimeMatches.filter((match) => match?.variantId).map((match) => [normalizedVariantId(match.variantId), match]),
      );
      compatibleVariants = vehicle ? variants.filter((variant) => Boolean(fitmentForVariant(variant))) : [];
      compatibleIds = new Set(compatibleVariants.map((variant) => String(variant.id)));
    };
    refreshVehicleContext();

    const vehicleFilterActive = () => purchaseRoute === "vehicle" && Boolean(vehicle);
    const selectableVariants = () => variants.filter(
      (variant) => variant.available && (!vehicleFilterActive() || compatibleIds.has(String(variant.id))),
    );
    const setOptionSelectors = (variant) => {
      if (!variant?.options) return;
      optionSelectors.forEach((selector, index) => {
        selector.value = variant.options[index] || "";
      });
    };
    const syncOptionInterfaces = () => {
      optionSelectors.forEach((selector) => {
        selector.dispatchEvent(new CustomEvent("dorimm:choice-sync"));
      });
    };
    const refreshOptionAvailability = () => {
      const selectable = selectableVariants();
      optionSelectors.forEach((selector, index) => {
        [...selector.options].forEach((option) => {
          option.disabled = !selectable.some(
            (variant) => variant.options?.[index] === option.value
              && optionSelectors.slice(0, index).every(
                (previousSelector, previousIndex) => variant.options?.[previousIndex] === previousSelector.value,
              ),
          );
        });
      });
    };
    const refreshVariantSelectAvailability = () => {
      if (variantSelect.tagName !== "SELECT") return;
      [...variantSelect.options].forEach((option) => {
        const variant = variantById(option.value);
        const fitmentCompatible = !vehicleFilterActive() || compatibleIds.has(String(option.value));
        option.disabled = !variant?.available || !fitmentCompatible;
        option.hidden = vehicleFilterActive() && !fitmentCompatible;
      });
    };
    const reconcileVariantForRoute = () => {
      const selectable = selectableVariants();
      const current = variantById(variantSelect.value);
      const next = current && selectable.some((variant) => String(variant.id) === String(current.id))
        ? current
        : selectable[0];
      if (next) {
        variantSelect.value = String(next.id);
        setOptionSelectors(next);
      }
      refreshVariantSelectAvailability();
      refreshOptionAvailability();
      syncOptionInterfaces();
    };
    const selectFromOptions = (changedIndex) => {
      const selectable = selectableVariants();
      const values = optionSelectors.map((selector) => selector.value);
      let variant = selectable.find((candidate) =>
        candidate.options?.every((value, index) => value === values[index]),
      );
      if (!variant && Number.isInteger(changedIndex)) {
        variant = selectable.find(
          (candidate) => candidate.options?.[changedIndex] === values[changedIndex]
            && values.slice(0, changedIndex).every(
              (value, index) => candidate.options?.[index] === value,
            ),
        );
      }
      if (!variant) variant = selectable[0];
      if (!variant) return;
      setOptionSelectors(variant);
      variantSelect.value = String(variant.id);
      refreshOptionAvailability();
      syncOptionInterfaces();
      variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const updateSelectedTotal = (variant) => {
      if (variant?.price == null) return;
      const quantity = Math.max(1, Number(quantityInput?.value) || 1);
      const formattedTotal = money.format((variant.price * quantity) / 100);
      if (totalPrice) totalPrice.textContent = formattedTotal;
      if (purchaseTotal) purchaseTotal.textContent = formattedTotal;
    };
    const setPurchaseStep = (activeIndex, completedThrough = -1) => {
      purchaseSteps.forEach((step, index) => {
        step.classList.toggle("is-active", index === activeIndex);
        step.classList.toggle("is-complete", index <= completedThrough);
      });
    };
    const setPurchasePrompt = (value) => {
      if (purchasePrompt) purchasePrompt.textContent = value;
    };
    const writeAllProperties = (values = {}) => {
      purchasePropertyKeys.forEach((key) => setProperty(form, key, values[key] ?? ""));
    };
    const setRouteUi = () => {
      form.dataset.purchaseRoute = purchaseRoute;
      root.dataset.purchaseRoute = purchaseRoute;
      routeButtons.forEach((button) => {
        const active = normalizeRoute(button.dataset.productRoute) === purchaseRoute;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
      if (flowBody) flowBody.hidden = !purchaseRoute;
      if (purchaseAction) purchaseAction.hidden = !purchaseRoute;
      if (expertReview) expertReview.hidden = purchaseRoute !== "expert";
      if (expertConfirmContainer) expertConfirmContainer.hidden = purchaseRoute !== "expert";
      if (expertConsent) {
        expertConsent.disabled = purchaseRoute !== "expert";
        expertConsent.required = purchaseRoute === "expert";
      }
      if (purchaseRoute !== "vehicle" && vehicleConfirm) vehicleConfirm.hidden = true;
      if (vehicleConsent) {
        vehicleConsent.disabled = purchaseRoute !== "vehicle";
        if (purchaseRoute !== "vehicle") vehicleConsent.required = false;
      }
    };
    const applyPurchaseGate = (ready, prompt) => {
      const isReady = Boolean(ready);
      form.dataset.purchaseReady = String(isReady);
      if (purchaseAction) {
        purchaseAction.dataset.purchaseReady = String(isReady);
        purchaseAction.dataset.purchaseRoute = purchaseRoute;
      }
      const locked = form.dataset.cartSubmitting === "true";
      atc.disabled = !isReady || locked;
      atc.setAttribute("aria-disabled", String(!isReady || locked));
      if (buyNow) {
        buyNow.disabled = !isReady || locked;
        buyNow.setAttribute("aria-disabled", String(!isReady || locked));
      }
      if (vehicleCta) vehicleCta.hidden = !(purchaseRoute === "vehicle" && !vehicle);
      setPurchasePrompt(prompt);
    };
    const resetConfirmations = () => {
      if (vehicleConsent) vehicleConsent.checked = false;
      if (expertConsent) expertConsent.checked = false;
      confirmedExpertVariantId = "";
    };
    const renderCommonVariant = (variant) => {
      updateProductSpecs(root, variant);
      updateSelectedTotal(variant);
      if (price && variant?.price != null) price.textContent = money.format(variant.price / 100);
      const summary = root.querySelector("[data-product-variant-summary]");
      if (summary && variant) {
        summary.innerHTML = `<small>${escapeHtml(message("selectedSku", "SELECTED SKU"))}</small><strong>${escapeHtml(variant.sku || "")}</strong><span>${escapeHtml(localizedVariantSummary(variant))}</span>`;
      }
    };

    const render = () => {
      const variant = variantById(variantSelect.value);
      renderCommonVariant(variant);
      setRouteUi();

      if (!purchaseRoute) {
        if (panel) panel.hidden = true;
        if (vehicleConfirm) vehicleConfirm.hidden = true;
        writeAllProperties({ wheel_sku: variant?.sku || "" });
        setPurchaseStep(0);
        applyPurchaseGate(false, message("choosePurchaseMethod", "CHOOSE HOW YOU WANT TO ORDER"));
        return;
      }

      if (purchaseRoute === "expert") {
        if (panel) panel.hidden = true;
        if (vehicleConfirm) vehicleConfirm.hidden = true;
        const exactSpecs = exactSpecsForVariant(variant);
        if (expertSummary) expertSummary.textContent = exactSpecs.summary || message("expertSpecIncomplete", "Complete the exact wheel specification");
        const confirmationMatches = Boolean(
          expertConsent?.checked && confirmedExpertVariantId === String(variant?.id || ""),
        );
        writeAllProperties({
          status: message("expertReviewStatus", "Expert-selected specification · Dorimm final technical review required"),
          wheel_sku: variant?.sku || "",
          rule: "expert-spec-review-v1",
          purchase_method: message("expertMethod", "Expert specification"),
          expert_specification: exactSpecs.complete ? exactSpecs.summary : "",
          specification_confirmation: confirmationMatches
            ? message("specificationConfirmationRecorded", "Customer confirmed exact specification before checkout")
            : "",
          purchase_route: "expert-spec-v1",
        });
        const ready = Boolean(
          orderable()
          && variant?.available
          && exactSpecs.complete
          && centerCapReady()
          && confirmationMatches,
        );
        const prompt = !orderable()
          ? message("outOfStock", "CURRENTLY UNAVAILABLE")
          : !exactSpecs.complete
            ? message("expertSpecIncomplete", "COMPLETE SIZE, PCD, ET, CENTER BORE AND FINISH")
            : !centerCapReady()
              ? centerCapPrompt
              : !confirmationMatches
                ? message("expertConfirmToContinue", "CONFIRM THE EXACT SPECIFICATION TO CONTINUE")
                : message("expertReady", "EXACT SPECIFICATION READY FOR FINAL REVIEW");
        setPurchaseStep(2, 1);
        applyPurchaseGate(ready, prompt);
        return;
      }

      if (panel) panel.hidden = false;
      const fitment = vehicle && variant ? fitmentForVariant(variant) : null;
      writeAllProperties({
        vehicle_id: vehicle?.id || "",
        vehicle: vehicle ? vehicleLabel(vehicle) : "",
        status: fitment ? data.policy.customerLabel : "",
        wheel_sku: variant?.sku || "",
        vehicle_pcd: vehicle?.pcd || "",
        recommended_tire: fitment?.tire || "",
        hub_ring: fitment?.hubRing || "",
        hardware: fitment?.hardware || "",
        rule: fitment?.rule || "",
        purchase_method: message("vehicleMethod", "Vehicle fitment"),
        purchase_route: "vehicle-fitment-v1",
      });
      const canReview = Boolean(orderable() && vehicle && fitment && variant?.available);
      if (vehicleConsent) {
        vehicleConsent.required = canReview;
        vehicleConsent.disabled = !canReview;
      }
      if (vehicleConfirm) vehicleConfirm.hidden = !canReview;
      const ready = Boolean(canReview && vehicleConsent?.checked && centerCapReady());

      if (!vehicle) {
        setPurchaseStep(0);
        panel.innerHTML = `
          <small>01 / ${escapeHtml(message("startWithCar", "START WITH YOUR CAR"))}</small>
          <strong>${escapeHtml(message("tellUsWhatYouDrive", "Tell us what you drive. We will narrow the real SKUs."))}</strong>
          <p>${escapeHtml(message("vehicleRuleHelp", "Year, make, model and exact trim determine which diameter, PCD, offset and center bore candidates remain available."))}</p>
          <a href="${escapeHtml(localizedPath("/pages/fitment"))}">${escapeHtml(message("openVehicleFinder", "OPEN VEHICLE FINDER"))} →</a>
        `;
        applyPurchaseGate(false, message("selectVehicle", "SELECT VEHICLE TO CONTINUE"));
      } else if (!compatibleVariants.length) {
        setPurchaseStep(1, 0);
        panel.innerHTML = `
          <small>01 / ${escapeHtml(message("noCandidate", "NO CURRENT CANDIDATE"))}</small>
          <strong>${escapeHtml(vehicleLabel(vehicle))}</strong>
          <p>${escapeHtml(message("noCandidateHelp", "This wheel currently has no SKU that passes the dimensional rule set for your vehicle."))}</p>
          <a href="${escapeHtml(localizedPath("/collections/all"))}?vehicle_id=${encodeURIComponent(vehicle.id)}">${escapeHtml(message("viewCandidates", "VIEW CANDIDATE WHEELS"))} →</a>
        `;
        applyPurchaseGate(false, message("noConfiguration", "NO CURRENT CONFIGURATION"));
      } else if (fitment) {
        setPurchaseStep(2, 1);
        panel.innerHTML = `
          <small>01 / ${escapeHtml(message("candidate", "DIMENSIONAL CANDIDATE"))}</small>
          <strong>${escapeHtml(vehicleLabel(vehicle))}</strong>
          <div class="product-fitment-facts">
            <span><small>${escapeHtml(message("wheelSku", "WHEEL SKU"))}</small><b>${escapeHtml(variant.sku)}</b></span>
            <span><small>${escapeHtml(message("tireReference", "TIRE REFERENCE"))}</small><b>${escapeHtml(fitment.tire || message("reviewRequired", "Review required"))}</b></span>
            <span><small>${escapeHtml(message("hub", "HUB"))}</small><b>${escapeHtml(fitment.hubRing || message("directReview", "Direct / review"))}</b></span>
          </div>
          <p>${escapeHtml(data.policy.notice)}</p>
          <a href="${escapeHtml(localizedPath("/pages/fitment"))}" data-change-product-vehicle>${escapeHtml(message("changeVehicle", "CHANGE VEHICLE"))} →</a>
        `;
        panel.querySelector("[data-change-product-vehicle]")?.addEventListener("click", clearVehicle);
        const prompt = !orderable()
          ? message("technicalReviewRequired", "REQUEST TECHNICAL REVIEW")
          : !centerCapReady()
            ? centerCapPrompt
            : vehicleConsent?.checked
              ? message("vehicleReady", message("addReviewed", "ADD REVIEWED CONFIGURATION"))
              : message("confirmReview", "CONFIRM REVIEW TO CONTINUE");
        applyPurchaseGate(ready, prompt);
      } else {
        setPurchaseStep(1, 0);
        panel.innerHTML = `
          <small>01 / ${escapeHtml(message("noCandidate", "NO CURRENT CANDIDATE"))}</small>
          <strong>${escapeHtml(vehicleLabel(vehicle))}</strong>
          <p>${escapeHtml(message("noCandidateHelp", "The selected configuration is not a dimensional candidate for this vehicle."))}</p>
        `;
        applyPurchaseGate(false, message("noConfiguration", "SELECT A MATCHED CONFIGURATION"));
      }
    };

    const setPurchaseRoute = (route) => {
      const nextRoute = normalizeRoute(route);
      if (!nextRoute || nextRoute === purchaseRoute) return;
      purchaseRoute = nextRoute;
      resetConfirmations();
      reconcileVariantForRoute();
      render();
    };
    const setQuantity = (rawValue, source = "preset") => {
      if (!quantityInput) return;
      const quantity = Math.min(99, Math.max(1, Math.round(Number(rawValue) || 1)));
      quantityInput.value = String(quantity);
      if (source === "preset") {
        if (customQuantity) {
          customQuantity.value = "";
          customQuantity.closest(".product-quantity-custom")?.classList.remove("is-active");
        }
        if (quantityPreset && !quantityPreset.value) {
          const matchingPreset = [...quantityPreset.options].find((option) => option.value === String(quantity));
          if (matchingPreset) quantityPreset.value = matchingPreset.value;
          quantityPreset.dispatchEvent(new CustomEvent("dorimm:choice-sync"));
        }
      } else {
        customQuantity?.closest(".product-quantity-custom")?.classList.add("is-active");
        if (quantityPreset) {
          quantityPreset.value = "";
          quantityPreset.dispatchEvent(new CustomEvent("dorimm:choice-sync"));
        }
      }
      updateSelectedTotal(variantById(variantSelect.value));
    };

    const requestedSku = new URLSearchParams(window.location.search).get("sku");
    const requested = requestedSku
      ? selectableVariants().find((variant) => variant.sku === requestedSku)
      : null;
    const current = variantById(variantSelect.value);
    const initial = requested
      || selectableVariants().find((variant) => String(variant.id) === String(current?.id))
      || selectableVariants()[0]
      || variants.find((variant) => variant.available)
      || variants[0];
    if (initial) {
      variantSelect.value = String(initial.id);
      setOptionSelectors(initial);
    }
    refreshVariantSelectAvailability();
    refreshOptionAvailability();
    syncOptionInterfaces();

    optionSelectors.forEach((selector, index) => {
      selector.addEventListener("change", () => selectFromOptions(index));
    });
    routeButtons.forEach((button) => {
      button.addEventListener("click", () => setPurchaseRoute(button.dataset.productRoute));
    });
    variantSelect.addEventListener("change", () => {
      resetConfirmations();
      render();
    });
    vehicleConsent?.addEventListener("change", render);
    expertConsent?.addEventListener("change", () => {
      confirmedExpertVariantId = expertConsent.checked ? String(variantSelect.value || "") : "";
      render();
    });
    quantityPreset?.addEventListener("change", () => setQuantity(quantityPreset.value, "preset"));
    customQuantity?.addEventListener("input", () => {
      if (!customQuantity.value) {
        setQuantity(quantityPreset?.value || 1, "preset");
        return;
      }
      setQuantity(customQuantity.value, "custom");
    });
    customQuantity?.addEventListener("change", () => {
      if (!customQuantity.value) return;
      setQuantity(customQuantity.value, "custom");
      customQuantity.value = quantityInput?.value || customQuantity.value;
    });
    form.addEventListener("dorimm:center-cap-change", render);
    form.addEventListener("dorimm:pdp-gate-refresh", render);

    const refreshFromVehicleState = (nextVehicle) => {
      refreshVehicleContext(nextVehicle);
      if (!purchaseRoute && vehicle) purchaseRoute = "vehicle";
      if (purchaseRoute === "vehicle") {
        if (vehicleConsent) vehicleConsent.checked = false;
        reconcileVariantForRoute();
      } else {
        refreshVariantSelectAvailability();
        refreshOptionAvailability();
        syncOptionInterfaces();
      }
      render();
    };
    const scheduleVehicleRefresh = (event) => queueMicrotask(() => {
      if (!root.isConnected) {
        document.removeEventListener("dorimm:vehicle-selected", scheduleVehicleRefresh);
        document.removeEventListener("dorimm:vehicle-cleared", scheduleVehicleRefresh);
        return;
      }
      const nextVehicle = event.type === "dorimm:vehicle-cleared"
        ? null
        : vehicleFromProfile(event.detail);
      refreshFromVehicleState(nextVehicle);
    });
    document.addEventListener("dorimm:vehicle-selected", scheduleVehicleRefresh);
    document.addEventListener("dorimm:vehicle-cleared", scheduleVehicleRefresh);
    render();
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-fitment-finder]").forEach(initFinder);
    scope
      .querySelectorAll("[data-collection-fitment]")
      .forEach(initCollectionFitment);
    scope.querySelectorAll("[data-product-fitment-root]").forEach(initProductFitment);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize(), {
      once: true,
    });
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) =>
    initialize(event.target),
  );
  document.addEventListener("click", (event) => {
    if (!event.target.closest("[data-custom-select]")) closeMenus();
  });
  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape") closeMenus();
  });

  window.DorimmFitment = {
    selectedVehicle,
    vehicleLabel,
    fitmentForSku,
    matchingSkus,
    clearVehicle,
  };
})();
